import React, { useEffect, useRef } from "react";

/** A date input that never re-renders while the browser's calendar is open.
 *
 *  Chrome fires React's onChange (the DOM `input` event) while you step months in its picker,
 *  and writing `value` back into a controlled input at that moment closes the picker and commits
 *  whatever day it had highlighted. So this input is UNCONTROLLED: the DOM owns the value while
 *  you work, React never touches it mid-interaction, and the save happens on the native `change`
 *  (a real pick / a finished keyboard entry), on blur, or on Enter. */
export default function CommitDateInput({ value, onCommit, className = "", wrapClassName = "inline-block", typedOnly = false, readOnly = false, ...rest }) {
  const ref = useRef(null);
  const last = useRef(value || "");
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  // Pull a new server value in only when the person is not in the field
  useEffect(() => {
    last.current = value || "";
    const el = ref.current;
    if (el && document.activeElement !== el && el.value !== (value || "")) el.value = value || "";
  }, [value]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const commit = () => {
      const v = el.value || "";
      if (v === last.current) return;
      // a partial keyboard entry has value "" in the DOM — never treat that as "cleared" mid-typing
      if (!v && document.activeElement === el) return;
      last.current = v;
      commitRef.current?.(v);
    };
    const onKey = (e) => { if (e.key === "Enter") { e.preventDefault(); commit(); el.blur(); } };
    const onBlur = () => {
      const v = el.value || "";
      if (typedOnly && !v) return;
      if (v !== last.current) { last.current = v; commitRef.current?.(v); }
    };
    if (!typedOnly) el.addEventListener("change", commit);
    el.addEventListener("blur", onBlur);
    el.addEventListener("keydown", onKey);
    return () => {
      el.removeEventListener("change", commit);
      el.removeEventListener("blur", onBlur);
      el.removeEventListener("keydown", onKey);
    };
  }, [typedOnly]);

  // The browser's date input always shows its own locale format. To read MM/DD/YY everywhere, the
  // native text is made transparent while idle and a label with the saved value sits on top; on
  // focus the native text returns so keyboard entry still works, and the calendar button is untouched.
  const textCls = className.split(/\s+/).filter((c) => /^(text-|font-)/.test(c)).join(" ");
  return (
    <span className={`relative align-middle ${wrapClassName}`}>
      <input ref={ref} type="date" defaultValue={value || ""} readOnly={readOnly} {...rest}
             className={`peer ${className} [&:not(:focus)]:text-transparent [color-scheme:light] dark:[color-scheme:dark] [&::-webkit-calendar-picker-indicator]:m-0 [&::-webkit-calendar-picker-indicator]:p-0 [&::-webkit-calendar-picker-indicator]:w-3 [&::-webkit-calendar-picker-indicator]:h-3 [&::-webkit-datetime-edit]:p-0`} />
      <span aria-hidden className={`pointer-events-none absolute inset-y-0 left-1 flex items-center tabular-nums peer-focus:hidden ${textCls || "text-gray-700"} ${value ? "" : "text-gray-400 font-normal"}`}>
        {fmtDMY(value) || "mm/dd/yy"}
      </span>
    </span>
  );
}

export const fmtDMY = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso || "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${m}/${d}/${y.slice(2)}`;
};
